import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, ArrowRight, CalendarPlus, FilePenLine, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { AccountIdBanner } from "@/components/AccountIdBanner";
import { ExtendModal } from "./ExtendModal";
import { ChangeRequestModal } from "./ChangeRequestModal";
import { api } from "@/lib/api";
import { payBooking } from "@/lib/payments";
import { fmtINR, fmtDate, fmtHour, STATUS_STYLES, STATUS_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const MyBookings = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extendFor, setExtendFor] = useState(null);
  const [changeFor, setChangeFor] = useState(null);
  const [pupCount, setPupCount] = useState(0);

  const load = async () => {
    try {
      const [b, p] = await Promise.all([api.get("/bookings/mine"), api.get("/pups")]);
      setBookings(b.data);
      setPupCount(p.data.length);
    } catch {
      toast.error("Could not load your bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const discount = (b) => b.discount_amount || 0;
  const balance = (b) => Math.max(0, b.total - discount(b) - b.amount_paid);
  const canExtend = (b) =>
    ["confirmed", "paid_full"].includes(b.status) && b.items.some((i) => i.unit === "night");
  const earliestCheckinMs = (b) =>
    Math.min(...b.items.map((i) => new Date(`${i.start_date}T${i.checkin_time || "11:00"}:00+05:30`).getTime()));
  const hasCheckedIn = (b) => b.items.length > 0 && earliestCheckinMs(b) <= Date.now();
  const canRequestChange = (b) =>
    !["completed", "cancelled"].includes(b.status) && b.change_request?.status !== "pending" && !hasCheckedIn(b);

  const payDue = async (booking) => {
    const kind = booking.status === "awaiting_payment" ? "advance" : "balance";
    try {
      const res = await payBooking({
        bookingId: booking.booking_id,
        kind,
        user,
        onDemo: async (payment) => {
          toast.info(`Demo mode — simulating payment of ${fmtINR(payment.amount)}.`);
          return true;
        },
      });
      if (res.done) {
        toast.success(kind === "advance" ? "Advance paid — slot reserved!" : "Balance cleared. All set!");
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-32">
        <p className="font-display text-xl text-navy">Loading your bookings…</p>
      </div>
    );
  }

  return (
    <section data-testid="my-bookings-page" className="pt-32 pb-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal y={14}>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
                <span className="h-px w-10 bg-bark" aria-hidden="true" />
                My account
              </p>
              <h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
                My <em className="font-editorial italic text-bark">bookings</em>
              </h1>
            </div>
            <button
              type="button"
              data-testid="book-service-button"
              onClick={() => (pupCount ? navigate("/account/book") : toast.info("Add your pup's profile first — it takes a minute."))}
              className="inline-flex items-center gap-2 rounded-full bg-bark px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
            >
              <CalendarCheck className="h-4 w-4" aria-hidden="true" />
              Book a service
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.03}>
          <AccountIdBanner user={user} setUser={setUser} />
        </Reveal>

        <Reveal delay={0.1}>
          {bookings.length === 0 ? (
            <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl border border-dashed border-ink/15 bg-white/60 py-16 text-center">
              <CalendarCheck className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <p className="max-w-sm text-sm text-slate-500">
                No bookings yet. Pick a service, choose your dates and reserve with a 50% advance.
              </p>
              <button
                type="button"
                data-testid="empty-book-button"
                onClick={() => (pupCount ? navigate("/account/book") : toast.info("Add your pup's profile first."))}
                className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink"
              >
                Book now
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="mt-10 space-y-5">
              {bookings.map((b) => (
                <div key={b.booking_id} data-testid={`booking-${b.booking_id}`} className="rounded-3xl border border-ink/8 bg-white p-7">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${STATUS_STYLES[b.status]}`}>
                        {STATUS_LABELS[b.status]}
                      </span>
                      {b.change_request?.status === "pending" && (
                        <span className="rounded-full bg-amberwarm/15 px-3.5 py-1.5 text-xs font-semibold text-amberwarm">
                          {b.change_request.type === "cancel" ? "Cancellation" : "Change"} requested
                        </span>
                      )}
                      {b.status === "cancelled" && b.refund_amount > 0 && (
                        <span className="rounded-full bg-slate-100 px-3.5 py-1.5 text-xs font-semibold text-slate-600">
                          Refund {fmtINR(b.refund_amount)} ({b.refund_policy === "full" ? "100%" : "75%"})
                        </span>
                      )}
                      <span className="text-xs text-slate-400">#{b.booking_id}</span>
                    </div>
                  </div>
                  <ul className="mt-5 space-y-2">
                    {b.items.map((i) => (
                      <li key={i.service_id} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                        <span className="font-medium text-ink">
                          {i.service_name}
                          <span className="ml-2 text-slate-500">
                            {fmtDate(i.start_date)}
                            {i.end_date !== i.start_date && ` → ${fmtDate(i.end_date)}`}
                            {i.checkin_time && ` · check-in ${fmtHour(i.checkin_time)}`}
                            {i.checkout_time && ` · check-out ${fmtHour(i.checkout_time)}`}
                            {(i.lines || []).some((l) => l.slot_label) &&
                              ` · ${(i.lines || []).filter((l) => l.slot_label).map((l) => l.slot_label).join(", ")}`}
                          </span>
                        </span>
                        <span className="text-xs text-slate-400">
                          {(i.lines || []).map((l) => l.pup_name).join(" · ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-ink/8 pt-5">
                    <p className="text-sm font-medium text-ink">
                      {fmtINR(b.total)} total
                      {discount(b) > 0 && (
                        <span className="text-emerald-600"> · {fmtINR(discount(b))} off{`${b.discount_note ? ` (${b.discount_note})` : ""}`}</span>
                      )}
                      {" "}· {fmtINR(b.amount_paid)} paid
                      {b.status !== "cancelled" && balance(b) > 0 && <span className="text-bark"> · {fmtINR(balance(b))} due</span>}
                    </p>
                    {b.change_request?.status === "pending" ? (
                      <span
                        data-testid={`request-pending-note-${b.booking_id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-amberwarm/15 px-4 py-2.5 text-xs font-semibold text-amberwarm"
                      >
                        <Clock className="h-4 w-4" aria-hidden="true" />
                        {b.change_request.type === "cancel"
                          ? "Cancellation request raised — awaiting review"
                          : "Change request raised — awaiting review"}
                      </span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3">
                        {canExtend(b) && (
                          <button
                            type="button"
                            data-testid={`extend-button-${b.booking_id}`}
                            onClick={() => setExtendFor(b)}
                            className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                          >
                            <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                            Extend stay
                          </button>
                        )}
                        {["awaiting_payment", "confirmed"].includes(b.status) && balance(b) > 0 && (
                          <button
                            type="button"
                            data-testid={`pay-button-${b.booking_id}`}
                            onClick={() => payDue(b)}
                            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold transition-colors duration-200 ${
                              b.status === "awaiting_payment"
                                ? "bg-bark text-white hover:bg-bark-dark"
                                : "border border-bark/40 text-bark hover:bg-bark hover:text-white"
                            }`}
                          >
                            {b.status === "awaiting_payment"
                              ? `Pay advance ${fmtINR(b.advance_target - b.amount_paid)}`
                              : `Clear balance ${fmtINR(balance(b))}`}
                          </button>
                        )}
                        {canRequestChange(b) && (
                          <button
                            type="button"
                            data-testid={`change-button-${b.booking_id}`}
                            onClick={() => setChangeFor(b)}
                            className="inline-flex items-center gap-2 rounded-full border border-ink/10 px-5 py-2.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-bark hover:text-bark"
                          >
                            <FilePenLine className="h-4 w-4" aria-hidden="true" />
                            Edit / cancel
                          </button>
                        )}
                        {!["completed", "cancelled"].includes(b.status) && hasCheckedIn(b) && (
                          <span data-testid={`checked-in-note-${b.booking_id}`} className="text-xs italic text-slate-400">
                            Checked in — call or WhatsApp us for changes
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Reveal>
      </div>

      <ExtendModal booking={extendFor} open={Boolean(extendFor)} onClose={() => setExtendFor(null)} onDone={() => load()} />
      <ChangeRequestModal booking={changeFor} open={Boolean(changeFor)} onClose={() => setChangeFor(null)} onDone={() => load()} />
    </section>
  );
};

export default MyBookings;
